import { fileToBase64 } from '@/components/custom/form'
import { useAppForm } from '@/lib/use-app-form'
import { useLingui } from '@lingui/react/macro'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { revalidateLogic } from '@tanstack/react-form'
import { usePostLoginRedirect } from '@/hooks/use-post-login-redirect'
import { flowViewTransition, withViewTransition } from '@/lib/utils'
import { trpc, type User } from '@/main'
import { useAppStore } from '@/lib/app-store'
import { useLists } from '@/hooks/use-lists'
import {
  aboutSchema,
  jobInfoSchema,
  locationSchema,
  neededProfileSteps,
  visibleProfileFields,
  type InitialData,
  type ProfileFieldKey,
  type ProfileStep,
} from 'shared/applicant-form'
import { Button } from '@/components/ui/button'
import { FlowFrame } from '@/routes/_app/-components/shared/flow-frame'
import { Collapse } from '@/components/custom/collapse'
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { HugeiconsIcon } from '@hugeicons/react'
import { CameraAddIcon } from '@hugeicons/core-free-icons'
import { RegistrationSteps } from '@/routes/_app/-components/shared/registration-steps'
import type { Callback } from 'shared/types'

export function ApplicantFormSection(props: {
  user: User
  initialData?: InitialData
  callback?: (args: Callback) => void
  initialStep?: ProfileStep
}) {
  const { t } = useLingui()
  const lists = useLists()
  const customize = useAppStore((state) => state.store.customize)
  const navigate = useNavigate()
  const redirectAfterAuth = usePostLoginRedirect()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())

  // The steps to ask, and which fields to show in each, are SNAPSHOTTED once (the saved user is the
  // source of truth) so saving a step mid-flow doesn't shrink the list and shift indices underneath
  // the wizard. Fully-prefilled steps (API/SDK) are dropped; partial steps render only their missing
  // fields, with linked fields kept together (see shared/applicant-form.visibleProfileFields).
  const [profileSteps] = useState(() => {
    const steps = neededProfileSteps(props.user)
    return steps.length ? steps : (['career'] as ProfileStep[])
  })
  const [visibleFields] = useState(() => visibleProfileFields(props.user))

  const initialIndex = props.initialStep ? Math.max(profileSteps.indexOf(props.initialStep), 0) : 0
  const [step, setStep] = useState(initialIndex)
  const stepRef = useRef(initialIndex)
  const currentSub: ProfileStep = profileSteps[step] ?? profileSteps[0] ?? 'career'
  const isLastStep = step === profileSteps.length - 1
  const showField = (key: ProfileFieldKey) => visibleFields[currentSub]?.includes(key) ?? false

  // Change sub-steps inside a view transition so the box morphs (fade out → border resize → fade in).
  // Mirror the index into a ref so the form `onSubmit` closures (created once) read the live step.
  const goToStep = (next: number | ((prev: number) => number)) => {
    const value = typeof next === 'function' ? next(stepRef.current) : next
    stepRef.current = value
    withViewTransition(() => setStep(value))
  }

  // Save the merged profile and leave the wizard (exit URL for SDK embeds, else the post-login target,
  // which is the report). Reads every step's form values, so it works regardless of which steps showed.
  const finalize = async () => {
    const { work, ...career } = careerForm.state.values
    const combined = {
      ...aboutForm.state.values,
      ...locationForm.state.values,
      ...career,
      // The career form nests company/employees under `work`, but the user record stores them flat —
      // and UserSchemaWithoutId strips the unknown `work` key. Flatten them (null when no prior work).
      company: work.workedPreviously ? work.company : null,
      employees: work.workedPreviously ? work.employees : null,
      image: await fileToBase64(aboutForm.state.values.image ?? undefined),
    }
    await updateUser(combined)
    props.callback?.({ action: 'registerApplicant', applicantId: props.user.id })

    if (customize.applicantButtonExit) {
      // External SDK exit URL (the customer's own site) — `navigate({ href })` detects the absolute URL
      // and hard-navigates to it (full document load), the correct behavior for leaving our app.
      navigate({ href: `${customize.applicantButtonExit}?applicantId=${props.user.id}` })
    } else {
      // Merge what we just saved over the (stale) user prop — the redirect gates on completeness.
      redirectAfterAuth({ ...props.user, ...combined })
    }
  }

  // True when the current step is the last one to show. Which step is last depends on which steps the
  // API/SDK prefilled, so the wizard can end on any of about/location/career — that step finalizes.
  const onLastStep = () => stepRef.current >= profileSteps.length - 1

  const sexOptions = [
    { value: 'male', label: t`Man` },
    { value: 'female', label: t`Woman` },
    { value: 'neutral', label: t`Other` },
  ]

  // Step 0 — About
  const aboutForm = useAppForm({
    validationLogic: revalidateLogic(),
    validators: { onDynamic: aboutSchema },
    defaultValues: {
      image: undefined as File | null | undefined,
      firstname: props.initialData?.firstname ?? props.user.firstname ?? '',
      lastname: props.initialData?.lastname ?? props.user.lastname ?? '',
      birthdate: props.initialData?.birthdate ?? props.user.birthdate ?? '',
      sex: props.initialData?.sex ?? props.user.sex,
    },
    onSubmit: async ({ value }) => {
      if (onLastStep()) return finalize()
      await updateUser({ ...value, image: await fileToBase64(value.image ?? undefined) })
      goToStep((s) => s + 1)
    },
  })

  // Step 1 — Location
  const locationForm = useAppForm({
    validationLogic: revalidateLogic(),
    validators: { onDynamic: locationSchema },
    defaultValues: {
      country: props.initialData?.country ?? props.user.country ?? '',
      province: props.initialData?.province ?? props.user.province ?? '',
      city: props.initialData?.city ?? props.user.city ?? '',
      cp: props.initialData?.cp ?? props.user.cp ?? '',
    },
    onSubmit: async ({ value }) => {
      if (onLastStep()) return finalize()
      await updateUser(value)
      goToStep((s) => s + 1)
    },
  })

  // Step 2 — Career (always the last step when shown → submits the whole profile)
  const careerForm = useAppForm({
    validationLogic: revalidateLogic(),
    validators: { onDynamic: jobInfoSchema },
    defaultValues: {
      work: {
        workedPreviously: props.initialData?.work?.workedPreviously ?? true,
        company: props.initialData?.work?.company ?? props.user.company ?? null,
        employees: props.initialData?.work?.employees ?? props.user.employees ?? null,
      },
      job: props.initialData?.job ?? props.user.job ?? '',
      educationalLevel: props.initialData?.educationalLevel ?? props.user.educationalLevel,
      level: props.initialData?.level ?? props.user.level,
      function: props.initialData?.function ?? props.user.function,
      sector: props.initialData?.sector ?? props.user.sector,
    },
    onSubmit: () => finalize(),
  })

  const stepMeta: Record<ProfileStep, { title: string; subtitle: string }> = {
    about: { title: t`Tell us about yourself`, subtitle: t`We'll use this to personalize your report` },
    location: { title: t`Where do you live?`, subtitle: t`We will use this data for statistical analysis` },
    career: { title: t`Your professional path`, subtitle: t`So we can identify your profile in the market` },
  }
  // Label names the NEXT step so it stays accurate when steps are skipped (about is always first, so
  // the next step is location or career). The last step finalizes regardless of which step it is.
  const nextSub = profileSteps[step + 1]
  const continueLabel = isLastStep
    ? t`Finish and view my profile`
    : nextSub === 'career'
      ? t`Continue to your career`
      : t`Continue to your location`

  const goBack = () => {
    if (step === 0) navigate({ to: '/test/personality', viewTransition: flowViewTransition() })
    else goToStep((s) => s - 1)
  }

  // `min-w-0 shrink` + truncate: the two buttons sit on one row inside the 480px frame, and a long
  // localized back label used to push the Continue button off the right edge instead of giving way.
  const backButton = (
    <Button type="button" variant="ghost" onClick={goBack} className="min-w-0 shrink">
      <ChevronLeftIcon />
      <span className="truncate">{step === 0 ? t`Back to test` : t`Back`}</span>
    </Button>
  )

  return (
    <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-6">
      <RegistrationSteps
        user={props.user}
        stage={{ kind: 'profile', sub: currentSub }}
        profileSteps={profileSteps}
        onProfileStepClick={(sub) => {
          const idx = profileSteps.indexOf(sub)
          if (idx >= 0) goToStep(idx)
        }}
      />

      <div className="flow-header text-center">
        <h1 className="text-2xl font-semibold">{stepMeta[currentSub].title}</h1>
        <p className="text-muted-foreground mt-1">{stepMeta[currentSub].subtitle}</p>
      </div>

      {/* The border (flow-frame) resizes between steps while the content (flow-content) fades out → in
          — both between routes and between the about/location/career sub-steps (via goToStep). */}
      <FlowFrame>
        <div className="flex flex-col gap-5">
          {currentSub === 'about' && (
            <form
              key="about"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                aboutForm.handleSubmit()
              }}
              className="flex flex-col gap-5"
            >
              {showField('image') && (
                <aboutForm.AppField
                  name="image"
                  children={(field) => (
                    <field.ImageField
                      label=""
                      variant="avatar"
                      uploadLabel={t`Profile photo`}
                      url={props.initialData?.image ?? props.user.image ?? undefined}
                      enableCrop
                      autoOpenEditor={false}
                      optimizeOnUpload
                      showRemove
                      centerIcon={<HugeiconsIcon icon={CameraAddIcon} size={24} className="text-muted-foreground" />}
                      aspectRatio={{ width: 128, height: 128 }}
                    />
                  )}
                />
              )}

              {showField('firstname') && (
                <aboutForm.AppField name="firstname" children={(field) => <field.TextField label={t`First name`} />} />
              )}
              {showField('lastname') && (
                <aboutForm.AppField name="lastname" children={(field) => <field.TextField label={t`Last name`} />} />
              )}
              {showField('birthdate') && (
                <aboutForm.AppField
                  name="birthdate"
                  children={(field) => <field.DateField label={t`Date of birth`} />}
                />
              )}
              {showField('sex') && (
                <aboutForm.AppField
                  name="sex"
                  children={(field) => <field.SimpleSelectField label={t`Sex`} options={sexOptions} />}
                />
              )}

              <div className="flex items-center justify-between gap-3">
                {backButton}
                <aboutForm.AppForm>
                  <aboutForm.SubmitButton label={continueLabel}>
                    {isLastStep ? <CheckIcon /> : <ChevronRightIcon />}
                  </aboutForm.SubmitButton>
                </aboutForm.AppForm>
              </div>
            </form>
          )}

          {currentSub === 'location' && (
            <form
              key="location"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                locationForm.handleSubmit()
              }}
              className="flex flex-col gap-5"
            >
              {/* country + province are linked (province options depend on country), so they show
                    together whenever either is missing. */}
              {showField('country') && (
                <>
                  <locationForm.AppField
                    name="country"
                    listeners={{ onChange: () => locationForm.setFieldValue('province', '') }}
                    children={(field) => <field.CountryField label={t`Country`} />}
                  />
                  <locationForm.Subscribe selector={(state) => state.values.country}>
                    {(country) => (
                      <locationForm.AppField
                        name="province"
                        children={(field) => <field.ProvinceField label={t`Province / State`} country={country} />}
                      />
                    )}
                  </locationForm.Subscribe>
                </>
              )}
              {showField('city') && (
                <locationForm.AppField name="city" children={(field) => <field.TextField label={t`City`} />} />
              )}
              {showField('cp') && (
                <locationForm.AppField name="cp" children={(field) => <field.TextField label={t`Postal code`} />} />
              )}

              <div className="flex items-center justify-between gap-3">
                {backButton}
                <locationForm.AppForm>
                  <locationForm.SubmitButton label={continueLabel}>
                    {isLastStep ? <CheckIcon /> : <ChevronRightIcon />}
                  </locationForm.SubmitButton>
                </locationForm.AppForm>
              </div>
            </form>
          )}

          {currentSub === 'career' && (
            <form
              key="career"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                careerForm.handleSubmit()
              }}
              className="flex flex-col gap-5"
            >
              <careerForm.Subscribe selector={(state) => state.values.work.workedPreviously}>
                {(workedPreviously) => (
                  <>
                    {/* The worked-before toggle drives company/employees AND the level options, so
                          it shows together with `level` (both pushed when the level is missing). */}
                    {showField('work') && (
                      <div className="flex flex-col">
                        <careerForm.AppField
                          name="work.workedPreviously"
                          listeners={{
                            onChange: ({ value: hasWorked }) => {
                              const level = careerForm.getFieldValue('level')
                              if (!hasWorked && level && level > 2) {
                                careerForm.resetField('level')
                              } else if (level != null) {
                                careerForm.setFieldValue('level', level)
                              }
                            },
                          }}
                          children={(field) => (
                            <field.SwitchField label={t`I have worked before`} orientation="horizontal" />
                          )}
                        />

                        <Collapse open={workedPreviously}>
                          {/* pt-5 is the gap to the switch — kept inside so it collapses too */}
                          <div className="flex flex-col gap-5 pt-5">
                            <careerForm.AppField
                              name="work.company"
                              children={(field) => <field.TextField label={t`Company name`} optional={false} />}
                            />
                            <careerForm.AppField
                              name="work.employees"
                              children={(field) => (
                                <field.SelectNumberField
                                  label={t`Company employees`}
                                  options={lists.employees.map((label, value) => ({ value, label }))}
                                  optional={false}
                                />
                              )}
                            />
                          </div>
                        </Collapse>
                      </div>
                    )}

                    {showField('job') && (
                      <careerForm.AppField
                        name="job"
                        children={(field) => (
                          <field.TextField label={workedPreviously ? t`Job title` : t`Preferred position`} />
                        )}
                      />
                    )}

                    {showField('educationalLevel') && (
                      <careerForm.AppField
                        name="educationalLevel"
                        children={(field) => (
                          <field.SimpleSelectNumberField
                            label={t`Studies`}
                            options={lists.educationalLevel.map((label, value) => ({ value, label }))}
                          />
                        )}
                      />
                    )}
                    {showField('level') && (
                      <careerForm.AppField
                        name="level"
                        children={(field) => (
                          <field.SimpleSelectNumberField
                            label={t`Professional level`}
                            options={
                              !workedPreviously
                                ? (lists.level[props.user.sex ?? 'neutral'] ?? lists.level.neutral)
                                    .slice(0, 2)
                                    .map((label, value) => ({ value, label }))
                                : (lists.level[props.user.sex ?? 'neutral'] ?? lists.level.neutral).map(
                                    (label, value) => ({ value, label }),
                                  )
                            }
                          />
                        )}
                      />
                    )}
                    {showField('function') && (
                      <careerForm.AppField
                        name="function"
                        children={(field) => (
                          <field.SelectNumberField
                            label={t`Function`}
                            options={lists.function.map((label, value) => ({ value, label }))}
                          />
                        )}
                      />
                    )}
                    {showField('sector') && (
                      <careerForm.AppField
                        name="sector"
                        children={(field) => (
                          <field.SelectNumberField
                            label={t`Sector`}
                            options={lists.sector.map((label, value) => ({ value, label }))}
                          />
                        )}
                      />
                    )}
                  </>
                )}
              </careerForm.Subscribe>

              <div className="flex items-center justify-between gap-3">
                {backButton}
                <careerForm.AppForm>
                  <careerForm.SubmitButton label={t`Finish and view my profile`}>
                    <CheckIcon />
                  </careerForm.SubmitButton>
                </careerForm.AppForm>
              </div>
            </form>
          )}
        </div>
      </FlowFrame>
    </div>
  )
}
